import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth, API } from '../App';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Package, Clock, CheckCircle, Truck, FileText, 
  ChevronRight, Loader2, User, Mail, DollarSign, Download,
  CreditCard, Calendar, MessageSquare, Bookmark, Trash2, Building2, Eye, Receipt, FileDown,
  MapPinned
} from 'lucide-react';

const CustomerDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [quoteRequests, setQuoteRequests] = useState([]);
  const [savedQuotes, setSavedQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [orderFiles, setOrderFiles] = useState([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [quoteDialogOpen, setQuoteDialogOpen] = useState(false);
  const [selectedQuoteRequest, setSelectedQuoteRequest] = useState(null);
  const [quoteRequestDialogOpen, setQuoteRequestDialogOpen] = useState(false);
  const [acceptingQuote, setAcceptingQuote] = useState(false);
  
  // Company dashboard state
  const [companyInfo, setCompanyInfo] = useState(null);
  const [viewMode, setViewMode] = useState('personal'); // 'personal' or 'company'
  const [companyOrders, setCompanyOrders] = useState([]);
  const [companyQuoteRequests, setCompanyQuoteRequests] = useState([]);
  const [loadingCompany, setLoadingCompany] = useState(false);
  
  // Show all orders/quotes toggle
  const [showAllOrders, setShowAllOrders] = useState(false);
  const [showAllQuotes, setShowAllQuotes] = useState(false);

  const fetchCompanyInfo = async () => {
    try {
      const response = await axios.get(`${API}/user/company`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
      });
      if (response.data.company) {
        setCompanyInfo({
          company: response.data.company,
          membership: response.data.membership
        });
      }
    } catch (error) {
      // User not in a company - that's fine
      console.log('No company membership found');
    }
  };

  const fetchData = async () => {
    try {
      const [ordersRes, quotesRes, savedRes] = await Promise.all([
        axios.get(`${API}/orders`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
        }),
        axios.get(`${API}/user/quote-requests`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
        }).catch(() => ({ data: { quote_requests: [] } })),
        axios.get(`${API}/quotes/saved`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
        }).catch(() => ({ data: { saved_quotes: [] } }))
      ]);
      setOrders(ordersRes.data.orders || []);
      setQuoteRequests(quotesRes.data.quote_requests || []);
      setSavedQuotes(savedRes.data.saved_quotes || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    }
    setLoading(false);
    
    // Also check if user belongs to a company
    fetchCompanyInfo();
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchCompanyData = async () => {
    if (!companyInfo) return;
    setLoadingCompany(true);
    try {
      const [ordersRes, quotesRes] = await Promise.all([
        axios.get(`${API}/user/company/orders`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
        }),
        axios.get(`${API}/user/company/quote-requests`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
        }).catch(() => ({ data: { quote_requests: [] } }))
      ]);
      setCompanyOrders(ordersRes.data.orders || []);
      setCompanyQuoteRequests(quotesRes.data.quote_requests || []);
    } catch (error) {
      toast.error('Failed to load company data');
    }
    setLoadingCompany(false);
  };

  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    setShowAllOrders(false);
    setShowAllQuotes(false);
    if (mode === 'company' && companyOrders.length === 0) {
      fetchCompanyData();
    }
  };

  const handleCompanyReorder = async (orderId) => {
    if (companyInfo?.membership?.role !== 'manager') {
      toast.error('Only managers can reorder from company orders');
      return;
    }
    try {
      const response = await axios.post(`${API}/user/company/orders/${orderId}/reorder`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
      });
      toast.success('Reorder created! Proceeding to checkout...');
      sessionStorage.setItem('quote', JSON.stringify({
        quote_id: response.data.quote_id,
        reordered: true
      }));
      navigate('/checkout');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create reorder');
    }
  };

  const loadSavedQuote = (savedQuote) => {
    // Store the quote in session storage and navigate to checkout
    const quoteData = {
      quote_id: savedQuote.quote_id,
      items: savedQuote.items,
      subtotal: savedQuote.subtotal,
      delivery_cost: savedQuote.delivery_cost,
      delivery_option_id: savedQuote.delivery_option_id,
      total: savedQuote.total
    };
    sessionStorage.setItem('quote', JSON.stringify(quoteData));
    toast.success('Quote loaded! Proceeding to checkout...');
    navigate('/checkout');
  };

  const viewSavedQuote = (savedQuote) => {
    setSelectedQuote(savedQuote);
    setQuoteDialogOpen(true);
  };

  const viewQuoteRequest = (quoteRequest) => {
    setSelectedQuoteRequest(quoteRequest);
    setQuoteRequestDialogOpen(true);
  };

  const acceptQuoteRequest = async (quoteRequest) => {
    // Build quote data from the quote request and save it to database
    setAcceptingQuote(true);
    try {
      // Use quote_breakdown if available, otherwise construct from files
      const breakdown = quoteRequest.quote_breakdown;
      const items = breakdown?.items?.map(item => ({
        file_name: item.name,
        material: item.material || '',
        volume_cm3: item.volume || 0,
        quantity: item.quantity || 1,
        unit_price: item.unit_price || 0,
        total_price: item.total_price || 0
      })) || quoteRequest.files?.map(f => ({
        file_name: f.name,
        material: f.material_name || '',
        volume_cm3: f.volume || 0,
        quantity: f.quantity || 1,
        total_price: f.quoted_price || 0
      })) || [];
      
      const subtotal = breakdown?.subtotal || quoteRequest.quoted_subtotal || quoteRequest.quoted_total || 0;
      const deliveryCost = breakdown?.delivery || quoteRequest.delivery_cost || 0;
      const total = quoteRequest.quoted_total || 0;
      
      let quoteId = quoteRequest.quote_id; // May already exist if previously accepted
      
      // Only call the accept endpoint if not already accepted
      if (quoteRequest.status !== 'accepted' || !quoteId) {
        const response = await axios.post(`${API}/quote-requests/${quoteRequest.request_id}/accept`, {
          items: items,
          subtotal: subtotal,
          delivery_cost: deliveryCost,
          delivery_option_id: quoteRequest.delivery_option_id || null,
          total: total
        }, {
          headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
        });
        quoteId = response.data.quote_id;
      }
      
      const quoteData = {
        quote_id: quoteId,
        items: items,
        subtotal: subtotal,
        delivery_cost: deliveryCost,
        delivery_option_id: quoteRequest.delivery_option_id || null,
        total: total,
        from_quote_request: true,
        quote_request_id: quoteRequest.request_id
      };
      
      sessionStorage.setItem('quote', JSON.stringify(quoteData));
      toast.success(quoteRequest.status === 'accepted' ? 'Proceeding to checkout...' : 'Quote accepted! Proceeding to checkout...');
      setQuoteRequestDialogOpen(false);
      navigate('/checkout');
    } catch (error) {
      console.error('Accept quote error:', error);
      toast.error(error.response?.data?.detail || 'Failed to process quote');
    }
    setAcceptingQuote(false);
  };

  const deleteSavedQuote = async (savedQuoteId) => {
    try {
      await axios.delete(`${API}/quotes/saved/${savedQuoteId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
      });
      toast.success('Quote deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete quote');
    }
  };

  const downloadSavedQuotePdf = async (savedQuoteId) => {
    try {
      toast.info('Generating PDF...');
      const response = await axios.get(`${API}/quotes/saved/${savedQuoteId}/pdf`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` },
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Quote_${savedQuoteId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Quote PDF downloaded');
    } catch (error) {
      console.error('Failed to download quote PDF:', error);
      toast.error('Failed to download quote PDF');
    }
  };

  const downloadSavedQuoteFile = async (savedQuoteId, fileId, fileName) => {
    try {
      const response = await axios.get(`${API}/quotes/saved/${savedQuoteId}/files/${fileId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName || `file_${fileId}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('File downloaded');
    } catch (error) {
      console.error('Failed to download file:', error);
      toast.error('Failed to download file');
    }
  };

  const viewOrder = (order) => {
    setSelectedOrder(order);
    setViewDialogOpen(true);
    fetchOrderFiles(order.order_id);
  };

  const fetchOrderFiles = async (orderId) => {
    setLoadingFiles(true);
    try {
      const response = await axios.get(`${API}/user/orders/${orderId}/files`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
      });
      setOrderFiles(response.data.files || []);
    } catch (error) {
      console.error('Failed to fetch files:', error);
      setOrderFiles([]);
    }
    setLoadingFiles(false);
  };

  const downloadFile = async (fileId, fileName) => {
    try {
      toast.info('Downloading file...');
      const response = await axios.get(`${API}/user/orders/${selectedOrder.order_id}/files/${fileId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('File downloaded!');
    } catch (error) {
      toast.error('Failed to download file');
    }
  };

  const downloadReceipt = async (orderId) => {
    try {
      toast.info('Generating receipt...');
      const response = await axios.get(`${API}/user/orders/${orderId}/receipt`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Receipt_${orderId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Receipt downloaded!');
    } catch (error) {
      toast.error('Failed to download receipt');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <Clock className="w-4 h-4" />;
      case 'confirmed': return <CheckCircle className="w-4 h-4" />;
      case 'printing': return <Package className="w-4 h-4" />;
      case 'ready_for_collection': return <MapPinned className="w-4 h-4" />;
      case 'shipped': return <Truck className="w-4 h-4" />;
      case 'delivered': return <CheckCircle className="w-4 h-4" />;
      default: return <FileText className="w-4 h-4" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20';
      case 'confirmed': return 'bg-blue-500/10 text-blue-500 border-blue-500/20';
      case 'printing': return 'bg-purple-500/10 text-purple-500 border-purple-500/20';
      case 'ready_for_collection': return 'bg-orange-500/10 text-orange-500 border-orange-500/20';
      case 'shipped': return 'bg-[#CCFF00]/10 text-[#CCFF00] border-[#CCFF00]/20';
      case 'delivered': return 'bg-green-500/10 text-green-500 border-green-500/20';
      case 'reviewing': return 'bg-blue-500/10 text-blue-500 border-blue-500/20';
      case 'quoted': return 'bg-[#CCFF00]/10 text-[#CCFF00] border-[#CCFF00]/20';
      default: return 'bg-white/10 text-white/70 border-white/20';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#CCFF00] animate-spin" />
      </div>
    );
  }

  const totalSpent = orders.reduce((sum, o) => sum + (o.total || 0), 0);
  const pendingQuotes = quoteRequests.filter(q => q.status === 'pending' || q.status === 'reviewing').length;
  const activeOrders = orders.filter(o => ['pending', 'confirmed', 'printing', 'shipped'].includes(o.order_status)).length;

  // Display data based on view mode
  const displayOrders = viewMode === 'company' ? companyOrders : orders;
  const displayQuoteRequests = viewMode === 'company' ? companyQuoteRequests : quoteRequests;
  const isManager = companyInfo?.membership?.role === 'manager';

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="customer-dashboard">
      <Navbar />
      
      <main className="pt-32 pb-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-12">
            <div>
              <span className="overline text-[#CCFF00]">Dashboard</span>
              <h1 className="text-3xl font-['Chivo'] font-black text-white mt-2">
                Welcome, {user?.name?.split(' ')[0] || 'Customer'}
              </h1>
              {companyInfo && (
                <p className="text-white/50 text-sm mt-1 flex items-center gap-2">
                  <Building2 className="w-4 h-4" />
                  {companyInfo.company.name} • {companyInfo.membership.role}
                </p>
              )}
            </div>
            <div className="flex items-center gap-3 mt-4 md:mt-0">
              {/* View Mode Toggle */}
              {companyInfo && (
                <Select value={viewMode} onValueChange={handleViewModeChange}>
                  <SelectTrigger className="w-[160px] bg-white/5 border-white/20 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#111] border-white/10">
                    <SelectItem value="personal" className="text-white">
                      <span className="flex items-center gap-2">
                        <User className="w-4 h-4" /> Personal
                      </span>
                    </SelectItem>
                    <SelectItem value="company" className="text-white">
                      <span className="flex items-center gap-2">
                        <Building2 className="w-4 h-4" /> {companyInfo.company.name}
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              )}
              <Link to="/profile">
                <Button variant="outline" className="border-white/20 text-white hover:bg-white/5 rounded-none">
                  <User className="w-4 h-4 mr-2" />
                  My Profile
                </Button>
              </Link>
              <Link to="/">
                <Button className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase tracking-wide rounded-none">
                  Order Now
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="bg-[#111] border border-white/10 p-6 rounded-sm">
              <div className="flex items-center justify-between mb-4">
                <Package className="w-6 h-6 text-[#CCFF00]" />
              </div>
              <p className="text-white/50 text-sm">{viewMode === 'company' ? 'Company Orders' : 'Total Orders'}</p>
              <p className="text-3xl font-bold text-white font-mono">{displayOrders.length}</p>
            </div>
            <div className="bg-[#111] border border-white/10 p-6 rounded-sm">
              <div className="flex items-center justify-between mb-4">
                <Clock className="w-6 h-6 text-yellow-500" />
              </div>
              <p className="text-white/50 text-sm">In Progress</p>
              <p className="text-3xl font-bold text-yellow-500 font-mono">
                {displayOrders.filter(o => ['pending', 'confirmed', 'printing', 'shipped'].includes(o.order_status)).length}
              </p>
            </div>
            <div className="bg-[#111] border border-white/10 p-6 rounded-sm">
              <div className="flex items-center justify-between mb-4">
                <Mail className="w-6 h-6 text-blue-500" />
              </div>
              <p className="text-white/50 text-sm">Pending Quotes</p>
              <p className="text-3xl font-bold text-blue-500 font-mono">
                {displayQuoteRequests.filter(q => q.status === 'pending' || q.status === 'reviewing').length}
              </p>
            </div>
            <div className="bg-[#111] border border-white/10 p-6 rounded-sm">
              <div className="flex items-center justify-between mb-4">
                <DollarSign className="w-6 h-6 text-green-500" />
              </div>
              <p className="text-white/50 text-sm">{viewMode === 'company' ? 'Company Spent' : 'Total Spent'}</p>
              <p className="text-3xl font-bold text-green-500 font-mono">
                ${displayOrders.reduce((sum, o) => sum + (o.total || 0), 0).toFixed(2)}
              </p>
            </div>
          </div>

          {/* Company View Loading */}
          {viewMode === 'company' && loadingCompany && (
            <div className="bg-[#111] border border-purple-500/30 p-8 rounded-sm mb-8 text-center">
              <Loader2 className="w-6 h-6 text-purple-500 mx-auto animate-spin mb-2" />
              <p className="text-white/50">Loading company data...</p>
            </div>
          )}

          {/* Company View Info Banner */}
          {viewMode === 'company' && !loadingCompany && (
            <div className="bg-purple-500/10 border border-purple-500/30 rounded-sm p-4 mb-8">
              <div className="flex items-center gap-3">
                <Building2 className="w-6 h-6 text-purple-500" />
                <div className="flex-1">
                  <h3 className="text-purple-400 font-bold">Company Dashboard - {companyInfo?.company?.name}</h3>
                  <p className="text-white/50 text-sm">
                    Viewing all orders from {companyInfo?.company?.name} team members.
                    {isManager ? ' You can reorder and accept quotes on behalf of the company.' : ' View-only access.'}
                  </p>
                </div>
                {isManager && (
                  <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30">Manager</Badge>
                )}
              </div>
            </div>
          )}

          {/* Quick Actions - Orders & Quotes Boxes */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            {/* Orders Box */}
            <div className="bg-[#111] border border-white/10 rounded-sm">
              <div className="p-4 border-b border-white/10 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Package className="w-5 h-5 text-[#CCFF00]" />
                  <h2 className="text-lg font-bold text-white">
                    {viewMode === 'company' ? 'Company Orders' : 'My Orders'}
                  </h2>
                </div>
                <Badge className="bg-white/10 text-white">{displayOrders.length}</Badge>
              </div>
              {displayOrders.length === 0 ? (
                <div className="p-8 text-center">
                  <Package className="w-10 h-10 text-white/20 mx-auto mb-3" />
                  <p className="text-white/50 text-sm mb-4">No orders yet</p>
                  <Link to="/">
                    <Button size="sm" className="bg-[#CCFF00] text-black hover:bg-[#B3E600] rounded-none">
                      Get Instant Quote
                    </Button>
                  </Link>
                </div>
              ) : (
                <div className="divide-y divide-white/10 max-h-[300px] overflow-y-auto">
                  {(showAllOrders ? displayOrders : displayOrders.slice(0, 5)).map((order) => (
                    <div 
                      key={order.order_id}
                      className="p-4 hover:bg-white/5 transition-colors cursor-pointer"
                      onClick={() => viewOrder(order)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-sm flex items-center justify-center ${getStatusColor(order.order_status)}`}>
                            {getStatusIcon(order.order_status)}
                          </div>
                          <div>
                            <p className="text-white font-mono text-sm font-bold">{order.order_id}</p>
                            <p className="text-white/50 text-xs">
                              {viewMode === 'company' && order.user_name && (
                                <span className="text-purple-400 mr-2">{order.user_name}</span>
                              )}
                              {new Date(order.created_at).toLocaleDateString('en-AU')}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-[#CCFF00] font-mono font-bold">${order.total?.toFixed(2)}</span>
                          {viewMode === 'company' && isManager && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={(e) => { e.stopPropagation(); e.preventDefault(); handleCompanyReorder(order.order_id); }}
                              className="text-purple-400 hover:text-purple-400 hover:bg-purple-400/10 h-7 px-2"
                              title="Reorder"
                            >
                              Reorder
                            </Button>
                          )}
                          <ChevronRight className="w-4 h-4 text-white/30" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {displayOrders.length > 5 && (
                <div 
                  className="p-3 border-t border-white/10 text-center cursor-pointer hover:bg-white/5 transition-colors"
                  onClick={() => setShowAllOrders(!showAllOrders)}
                >
                  <span className="text-[#CCFF00] text-sm font-medium">
                    {showAllOrders ? 'Show less' : `+ ${displayOrders.length - 5} more orders`}
                  </span>
                </div>
              )}
            </div>

            {/* Quotes Box */}
            <div className="bg-[#111] border border-white/10 rounded-sm">
              <div className="p-4 border-b border-white/10 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-[#CCFF00]" />
                  <h2 className="text-lg font-bold text-white">
                    {viewMode === 'company' ? 'Company Quotes' : 'Quote Requests'}
                  </h2>
                </div>
                <Badge className="bg-white/10 text-white">{displayQuoteRequests.length}</Badge>
              </div>
              {displayQuoteRequests.length === 0 ? (
                <div className="p-8 text-center">
                  <MessageSquare className="w-10 h-10 text-white/20 mx-auto mb-3" />
                  <p className="text-white/50 text-sm mb-4">No quote requests</p>
                  <Link to="/">
                    <Button size="sm" className="bg-[#CCFF00] text-black hover:bg-[#B3E600] rounded-none">
                      Request a Quote
                    </Button>
                  </Link>
                </div>
              ) : (
                <div className="divide-y divide-white/10 max-h-[300px] overflow-y-auto">
                  {(showAllQuotes ? displayQuoteRequests : displayQuoteRequests.slice(0, 5)).map((quote) => (
                    <div 
                      key={quote.request_id}
                      className="p-4 hover:bg-white/5 transition-colors cursor-pointer"
                      onClick={() => viewQuoteRequest(quote)}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-white font-mono text-sm font-bold">{quote.request_id}</p>
                          <p className="text-white/50 text-xs">
                            {viewMode === 'company' && quote.customer_name && (
                              <span className="text-purple-400 mr-2">{quote.customer_name}</span>
                            )}
                            {quote.files?.length || 0} file(s) • {new Date(quote.created_at).toLocaleDateString('en-AU')}
                          </p>
                        </div>
                        <div className="flex items-center gap-3">
                          <Badge variant="outline" className={`capitalize ${getStatusColor(quote.status)}`}>
                            {quote.status}
                          </Badge>
                          {quote.quoted_total && (
                            <span className="text-[#CCFF00] font-mono font-bold">${quote.quoted_total?.toFixed(2)}</span>
                          )}
                          <ChevronRight className="w-4 h-4 text-white/30" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {displayQuoteRequests.length > 5 && (
                <div 
                  className="p-3 border-t border-white/10 text-center cursor-pointer hover:bg-white/5 transition-colors"
                  onClick={() => setShowAllQuotes(!showAllQuotes)}
                >
                  <span className="text-[#CCFF00] text-sm font-medium">
                    {showAllQuotes ? 'Show less' : `+ ${displayQuoteRequests.length - 5} more quotes`}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Saved Quotes Section - Only show in personal view */}
          {viewMode === 'personal' && savedQuotes.length > 0 && (
            <div className="bg-[#111] border border-white/10 rounded-sm mb-8">
              <div className="p-4 border-b border-white/10 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Bookmark className="w-5 h-5 text-[#CCFF00]" />
                  <h2 className="text-lg font-bold text-white">Saved Quotes</h2>
                </div>
                <Badge className="bg-[#CCFF00]/10 text-[#CCFF00]">{savedQuotes.length}</Badge>
              </div>
              <div className="divide-y divide-white/10">
                {savedQuotes.map((sq) => (
                  <div 
                    key={sq.saved_quote_id}
                    className="p-4 hover:bg-white/5 transition-colors cursor-pointer"
                    onClick={() => viewSavedQuote(sq)}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-white font-medium">{sq.name}</p>
                        <p className="text-white/50 text-xs">
                          {sq.items?.length || 0} item(s) • Saved {new Date(sq.created_at).toLocaleDateString('en-AU')}
                        </p>
                        <p className="text-white/30 text-xs">
                          Expires: {new Date(sq.expires_at).toLocaleDateString('en-AU')}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-[#CCFF00] font-mono font-bold mr-2">${sq.total?.toFixed(2)}</span>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(e) => { e.stopPropagation(); downloadSavedQuotePdf(sq.saved_quote_id); }}
                          className="text-white/70 hover:text-[#CCFF00] hover:bg-white/5"
                          title="Download Quote PDF"
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); loadSavedQuote(sq); }}
                          className="bg-[#CCFF00] text-black hover:bg-[#B3E600] rounded-none"
                        >
                          <ChevronRight className="w-4 h-4 mr-1" />
                          Continue
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(e) => { e.stopPropagation(); deleteSavedQuote(sq.saved_quote_id); }}
                          className="text-red-400 hover:text-red-400 hover:bg-red-400/10"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Quick Links */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Link to="/" className="block">
              <div className="bg-[#111] border border-white/10 p-6 rounded-sm hover:border-[#CCFF00]/50 transition-colors group">
                <FileText className="w-8 h-8 text-[#CCFF00] mb-4 group-hover:scale-110 transition-transform" />
                <h3 className="text-white font-bold mb-2">Get Instant Quote</h3>
                <p className="text-white/50 text-sm">Upload parts for instant pricing</p>
              </div>
            </Link>

            <Link to="/profile" className="block">
              <div className="bg-[#111] border border-white/10 p-6 rounded-sm hover:border-[#CCFF00]/50 transition-colors group">
                <User className="w-8 h-8 text-[#CCFF00] mb-4 group-hover:scale-110 transition-transform" />
                <h3 className="text-white font-bold mb-2">My Profile</h3>
                <p className="text-white/50 text-sm">Update personal & billing info</p>
              </div>
            </Link>

            <a href="https://www.3dprintbureau.au/contact" className="block">
              <div className="bg-[#111] border border-white/10 p-6 rounded-sm hover:border-[#CCFF00]/50 transition-colors group">
                <Mail className="w-8 h-8 text-[#CCFF00] mb-4 group-hover:scale-110 transition-transform" />
                <h3 className="text-white font-bold mb-2">Contact Support</h3>
                <p className="text-white/50 text-sm">Get help with your orders</p>
              </div>
            </a>
          </div>
        </div>
      </main>

      {/* View Order Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="bg-[#111] border-white/10 text-white max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Package className="w-5 h-5 text-[#CCFF00]" />
                Order {selectedOrder?.order_id}
              </div>
              <Badge variant="outline" className={`capitalize ${getStatusColor(selectedOrder?.order_status)}`}>
                {selectedOrder?.order_status?.replace('-', ' ')}
              </Badge>
            </DialogTitle>
          </DialogHeader>
          
          {selectedOrder && (
            <div className="space-y-6 py-4">
              {/* Quick Actions - Download Receipt and Files */}
              <div className="flex flex-wrap gap-2">
                <Button
                  onClick={() => downloadReceipt(selectedOrder.order_id)}
                  className="bg-[#CCFF00] text-black hover:bg-[#b8e600] rounded-none"
                >
                  <Receipt className="w-4 h-4 mr-2" />
                  Download Receipt
                </Button>
                {orderFiles.length > 0 && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      orderFiles.forEach(file => downloadFile(file.id, file.name));
                    }}
                    className="border-white/20 text-white hover:bg-white/10 rounded-none"
                  >
                    <FileDown className="w-4 h-4 mr-2" />
                    Download All Files ({orderFiles.length})
                  </Button>
                )}
              </div>

              {/* Order Items with Download */}
              <div className="bg-white/5 rounded-sm p-4">
                <h3 className="text-white font-bold mb-3 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-[#CCFF00]" />
                  Order Items ({selectedOrder.items?.length || 0})
                </h3>
                <div className="space-y-3">
                  {selectedOrder.items?.map((item, idx) => (
                    <div key={idx} className="flex justify-between items-center py-2 border-b border-white/10 last:border-0">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <p className="text-white font-medium">{item.file_name}</p>
                          {orderFiles.find(f => f.name === item.file_name) && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-[#CCFF00] hover:text-[#CCFF00] hover:bg-[#CCFF00]/10 h-6 w-6 p-0"
                              onClick={() => {
                                const file = orderFiles.find(f => f.name === item.file_name);
                                if (file) downloadFile(file.id, file.name);
                              }}
                              title="Download File"
                            >
                              <Download className="w-3 h-3" />
                            </Button>
                          )}
                        </div>
                        <p className="text-white/50 text-sm">
                          {item.material} • {item.volume_cm3?.toFixed(2)} cm³ • Qty: {item.quantity}
                        </p>
                      </div>
                      <p className="text-[#CCFF00] font-mono">${item.total_price?.toFixed(2)}</p>
                    </div>
                  ))}
                </div>
                {loadingFiles && (
                  <div className="text-center py-2">
                    <Loader2 className="w-4 h-4 text-[#CCFF00] mx-auto animate-spin" />
                  </div>
                )}
              </div>

              {/* Payment Info */}
              <div className="bg-white/5 rounded-sm p-4">
                <h3 className="text-white font-bold mb-3 flex items-center gap-2">
                  <CreditCard className="w-4 h-4 text-[#CCFF00]" />
                  Payment Summary
                </h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-white/50">Subtotal</span>
                    <span className="text-white">${selectedOrder.subtotal?.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/50">Delivery</span>
                    <span className="text-white">${selectedOrder.delivery_cost?.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between pt-2 border-t border-white/10">
                    <span className="text-white font-bold">Total</span>
                    <span className="text-[#CCFF00] font-bold font-mono">${selectedOrder.total?.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between pt-2">
                    <span className="text-white/50">Payment Method</span>
                    <span className="text-white capitalize">{selectedOrder.payment_method?.replace('_', ' ')}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/50">Payment Status</span>
                    <Badge className={selectedOrder.payment_status === 'paid' ? 'bg-green-500/10 text-green-400' : 'bg-yellow-500/10 text-yellow-400'}>
                      {selectedOrder.payment_status}
                    </Badge>
                  </div>
                  {/* Xero Invoice Reference - shown to customer for invoice orders */}
                  {selectedOrder.payment_method === 'invoice' && selectedOrder.xero_invoice_number && (
                    <div className="flex justify-between pt-2 border-t border-white/10">
                      <span className="text-white/50">Xero Invoice #</span>
                      <span className="text-purple-400 font-mono font-bold">{selectedOrder.xero_invoice_number}</span>
                    </div>
                  )}
                  {/* Xero Invoice PDF Download */}
                  {selectedOrder.payment_method === 'invoice' && selectedOrder.xero_invoice_file && (
                    <div className="flex justify-between items-center pt-2 border-t border-white/10">
                      <span className="text-white/50">Invoice PDF</span>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => window.open(`${API}/orders/${selectedOrder.order_id}/xero-invoice?token=${localStorage.getItem('auth_token')}`, '_blank')}
                        className="text-purple-400 hover:text-purple-400 hover:bg-purple-400/10 h-7 px-2"
                      >
                        <Download className="w-4 h-4 mr-1" />
                        View Invoice
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              {/* Order Meta */}
              <div className="flex items-center gap-4 text-sm text-white/50">
                <div className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  Ordered: {new Date(selectedOrder.created_at).toLocaleString('en-AU')}
                </div>
              </div>

              {/* Linked Quote Request Reference */}
              {selectedOrder.from_quote_request && selectedOrder.quote_request_id && (
                <div className="bg-blue-500/10 border border-blue-500/20 rounded-sm p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-blue-400 text-sm font-medium">From Quote Request</p>
                      <p className="text-white font-mono font-bold">{selectedOrder.quote_request_id}</p>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => {
                        setViewDialogOpen(false);
                        const linkedQR = quoteRequests.find(qr => qr.request_id === selectedOrder.quote_request_id);
                        if (linkedQR) {
                          viewQuoteRequest(linkedQR);
                        }
                      }}
                      className="bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 rounded-none"
                    >
                      <MessageSquare className="w-4 h-4 mr-2" />
                      View Request
                    </Button>
                  </div>
                </div>
              )}

              {/* Contact Support */}
              <div className="pt-4 border-t border-white/10">
                <p className="text-white/50 text-sm text-center">
                  Questions about your order? <a href="https://www.3dprintbureau.au/contact" className="text-[#CCFF00] hover:underline">Contact support</a>
                </p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* View Saved Quote Dialog */}
      <Dialog open={quoteDialogOpen} onOpenChange={setQuoteDialogOpen}>
        <DialogContent className="bg-[#111] border-white/10 text-white max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <Bookmark className="w-5 h-5 text-[#CCFF00]" />
              {selectedQuote?.name || 'Saved Quote'}
            </DialogTitle>
          </DialogHeader>
          
          {selectedQuote && (
            <div className="space-y-6 py-4">
              {/* Quote Items */}
              <div className="bg-white/5 rounded-sm p-4">
                <h3 className="text-white font-bold mb-3 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-[#CCFF00]" />
                  Quote Items ({selectedQuote.items?.length || 0})
                </h3>
                <div className="space-y-3">
                  {selectedQuote.items?.map((item, idx) => (
                    <div key={idx} className="flex justify-between items-center py-2 border-b border-white/10 last:border-0">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <p className="text-white font-medium">{item.file_name}</p>
                          {item.file_id && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => downloadSavedQuoteFile(selectedQuote.saved_quote_id, item.file_id, item.file_name)}
                              className="h-6 px-2 text-white/50 hover:text-[#CCFF00] hover:bg-white/5"
                              title="Download file"
                            >
                              <Download className="w-3 h-3" />
                            </Button>
                          )}
                        </div>
                        <p className="text-white/50 text-sm">
                          {item.material && `${item.material} • `}
                          {item.volume_cm3 && `${item.volume_cm3?.toFixed(2)} cm³ • `}
                          Qty: {item.quantity}
                        </p>
                      </div>
                      <p className="text-[#CCFF00] font-mono">${item.total_price?.toFixed(2)}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Quote Summary */}
              <div className="bg-white/5 rounded-sm p-4">
                <h3 className="text-white font-bold mb-3 flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-[#CCFF00]" />
                  Price Summary
                </h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-white/50">Subtotal</span>
                    <span className="text-white">${selectedQuote.subtotal?.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/50">Delivery</span>
                    <span className="text-white">${selectedQuote.delivery_cost?.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between pt-2 border-t border-white/10">
                    <span className="text-white font-bold">Total</span>
                    <span className="text-[#CCFF00] font-bold font-mono">${selectedQuote.total?.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {/* Quote Meta */}
              <div className="flex flex-wrap items-center gap-4 text-sm text-white/50">
                <div className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  Saved: {new Date(selectedQuote.created_at).toLocaleDateString('en-AU')}
                </div>
                <div className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  Expires: {new Date(selectedQuote.expires_at).toLocaleDateString('en-AU')}
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4 border-t border-white/10">
                <Button
                  onClick={() => { setQuoteDialogOpen(false); loadSavedQuote(selectedQuote); }}
                  className="flex-1 bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase tracking-wide rounded-none"
                >
                  <ChevronRight className="w-4 h-4 mr-2" />
                  Continue to Checkout
                </Button>
                <Button
                  variant="outline"
                  onClick={() => { setQuoteDialogOpen(false); deleteSavedQuote(selectedQuote.saved_quote_id); }}
                  className="border-red-400/50 text-red-400 hover:bg-red-400/10 rounded-none"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* View Quote Request Dialog */}
      <Dialog open={quoteRequestDialogOpen} onOpenChange={setQuoteRequestDialogOpen}>
        <DialogContent className="bg-[#111] border-white/10 text-white max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <MessageSquare className="w-5 h-5 text-[#CCFF00]" />
                Quote Request {selectedQuoteRequest?.request_id}
              </div>
              <Badge variant="outline" className={`capitalize ${getStatusColor(selectedQuoteRequest?.status)}`}>
                {selectedQuoteRequest?.status}
              </Badge>
            </DialogTitle>
          </DialogHeader>
          
          {selectedQuoteRequest && (
            <div className="space-y-6 py-4">
              {/* Status Message */}
              {selectedQuoteRequest.status === 'pending' || selectedQuoteRequest.status === 'reviewing' ? (
                <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-sm p-4">
                  <p className="text-yellow-400 text-sm">
                    <Clock className="w-4 h-4 inline mr-2" />
                    Your quote request is being reviewed. We will notify you when pricing is ready.
                  </p>
                </div>
              ) : selectedQuoteRequest.status === 'quoted' ? (
                <div className="bg-[#CCFF00]/10 border border-[#CCFF00]/20 rounded-sm p-4">
                  <p className="text-[#CCFF00] text-sm">
                    <CheckCircle className="w-4 h-4 inline mr-2" />
                    Your quote is ready! Review the pricing below and proceed to checkout.
                  </p>
                </div>
              ) : selectedQuoteRequest.status === 'accepted' ? (
                <div className="bg-blue-500/10 border border-blue-500/20 rounded-sm p-4">
                  <p className="text-blue-400 text-sm">
                    <CheckCircle className="w-4 h-4 inline mr-2" />
                    You have accepted this quote. Click below to continue to checkout.
                  </p>
                </div>
              ) : selectedQuoteRequest.status === 'declined' ? (
                <div className="bg-red-500/10 border border-red-500/20 rounded-sm p-4">
                  <p className="text-red-400 text-sm">
                    This quote request was declined. Please contact support for more information.
                  </p>
                </div>
              ) : null}

              {/* Request Items */}
              <div className="bg-white/5 rounded-sm p-4">
                <h3 className="text-white font-bold mb-3 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-[#CCFF00]" />
                  Requested Items ({selectedQuoteRequest.files?.length || selectedQuoteRequest.quote_breakdown?.items?.length || 0})
                </h3>
                <div className="space-y-3">
                  {((selectedQuoteRequest.status === 'quoted' || selectedQuoteRequest.status === 'accepted') && selectedQuoteRequest.quote_breakdown?.items ? 
                    selectedQuoteRequest.quote_breakdown.items : 
                    selectedQuoteRequest.files
                  )?.map((item, idx) => {
                    const fileName = item.name || item.file_name;
                    const material = item.material || item.material_name;
                    const volume = item.volume;
                    const quantity = item.quantity || 1;
                    const price = item.total_price || item.quoted_price;
                    
                    return (
                      <div key={idx} className="flex justify-between items-center py-2 border-b border-white/10 last:border-0">
                        <div className="flex-1">
                          <p className="text-white font-medium">{fileName}</p>
                          <p className="text-white/50 text-sm">
                            {material && `${material} • `}
                            {volume && `${Number(volume).toFixed(2)} cm³ • `}
                            Qty: {quantity}
                          </p>
                        </div>
                        {price ? (
                          <p className="text-[#CCFF00] font-mono">${Number(price).toFixed(2)}</p>
                        ) : (
                          <p className="text-white/30 font-mono text-sm">Pending</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Price Summary - Only show if quoted or accepted */}
              {(selectedQuoteRequest.status === 'quoted' || selectedQuoteRequest.status === 'accepted') && selectedQuoteRequest.quoted_total && (
                <div className="bg-white/5 rounded-sm p-4">
                  <h3 className="text-white font-bold mb-3 flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-[#CCFF00]" />
                    Quote Summary
                  </h3>
                  <div className="space-y-2 text-sm">
                    {(selectedQuoteRequest.quote_breakdown?.subtotal || selectedQuoteRequest.quoted_subtotal) && (
                      <div className="flex justify-between">
                        <span className="text-white/50">Subtotal</span>
                        <span className="text-white">${(selectedQuoteRequest.quote_breakdown?.subtotal || selectedQuoteRequest.quoted_subtotal)?.toFixed(2)}</span>
                      </div>
                    )}
                    {(selectedQuoteRequest.quote_breakdown?.delivery || selectedQuoteRequest.delivery_cost) > 0 && (
                      <div className="flex justify-between">
                        <span className="text-white/50">Delivery</span>
                        <span className="text-white">${(selectedQuoteRequest.quote_breakdown?.delivery || selectedQuoteRequest.delivery_cost)?.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between pt-2 border-t border-white/10">
                      <span className="text-white font-bold">Total</span>
                      <span className="text-[#CCFF00] font-bold font-mono">${selectedQuoteRequest.quoted_total?.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Customer Notes */}
              {selectedQuoteRequest.notes && (
                <div className="bg-white/5 rounded-sm p-4">
                  <h3 className="text-white font-bold mb-2 text-sm">Your Notes</h3>
                  <p className="text-white/70 text-sm">{selectedQuoteRequest.notes}</p>
                </div>
              )}

              {/* Admin Response */}
              {selectedQuoteRequest.admin_notes && (
                <div className="bg-blue-500/10 border border-blue-500/20 rounded-sm p-4">
                  <h3 className="text-blue-400 font-bold mb-2 text-sm">Response from Team</h3>
                  <p className="text-white/70 text-sm">{selectedQuoteRequest.admin_notes}</p>
                </div>
              )}

              {/* Request Meta */}
              <div className="flex flex-wrap items-center gap-4 text-sm text-white/50">
                <div className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  Requested: {new Date(selectedQuoteRequest.created_at).toLocaleDateString('en-AU')}
                </div>
              </div>

              {/* Linked Order Reference */}
              {selectedQuoteRequest.status === 'ordered' && selectedQuoteRequest.order_id && (
                <div className="bg-green-500/10 border border-green-500/20 rounded-sm p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-green-400 text-sm font-medium">Order Created</p>
                      <p className="text-white font-mono font-bold">{selectedQuoteRequest.order_id}</p>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => {
                        setQuoteRequestDialogOpen(false);
                        const linkedOrder = orders.find(o => o.order_id === selectedQuoteRequest.order_id);
                        if (linkedOrder) {
                          viewOrder(linkedOrder);
                        }
                      }}
                      className="bg-green-500/20 text-green-400 hover:bg-green-500/30 rounded-none"
                    >
                      <Package className="w-4 h-4 mr-2" />
                      View Order
                    </Button>
                  </div>
                </div>
              )}

              {/* Actions */}
              {(selectedQuoteRequest.status === 'quoted' || selectedQuoteRequest.status === 'accepted') && (
                <div className="flex gap-3 pt-4 border-t border-white/10">
                  <Button
                    onClick={() => acceptQuoteRequest(selectedQuoteRequest)}
                    disabled={acceptingQuote}
                    className="flex-1 bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase tracking-wide rounded-none"
                  >
                    {acceptingQuote ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <ChevronRight className="w-4 h-4 mr-2" />
                    )}
                    {selectedQuoteRequest.status === 'accepted' ? 'Continue to Checkout' : 'Accept & Proceed to Checkout'}
                  </Button>
                </div>
              )}

              {/* Contact Support */}
              <div className="pt-4 border-t border-white/10">
                <p className="text-white/50 text-sm text-center">
                  Questions? <a href="https://www.3dprintbureau.au/contact" className="text-[#CCFF00] hover:underline">Contact support</a>
                </p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
};

export default CustomerDashboard;
